import { type BTServiceFileUpload } from "legacyComponents/FileUploadContainer.types";
import moment, { type Moment } from "moment";

import type { CalendarFeedItemViewModel } from "@btnet/services-btnet/handlers/models/calendarFeedItemViewModel";
import type { Int32BTServiceGroupedListItem } from "@btnet/services-btnet/handlers/models/int32BTServiceGroupedListItem";

import { BTSelectItem, type ServiceValidation } from "types/apiResponse/apiResponse";
import { type Countries } from "types/countries";
import {
    CheckAllState,
    type CustomFieldAssociatedType,
    type InternalUserActivationStatus,
    NotificationTypes,
    TabPermissions,
} from "types/enum";

import { getCheckAllState } from "commonComponents/btWrappers/BTTable/BTTable.utils";
import { type LinkingAction } from "commonComponents/entity/accounting/AccountingLinkingOptions/AccountingLinkingOptions.api.types";
import { type AccountingEntity } from "commonComponents/entity/accounting/LinkedAccountingEntity/LinkedAccountingEntity.api.types";
import { CellEmailLookupEntity } from "commonComponents/entity/cellEmailLookup/CellEmailLookup/CellEmailLookup.api.types";
import {
    type ICellPhoneInputFormValues,
    type SmsOptInStatus,
    type VerificationMessageType,
} from "commonComponents/entity/cellPhone/CellPhoneInput/CellPhoneInput.types";
import { type ICustomFieldFormValues } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer";
import {
    type ICustomFieldOptionsList,
    type ICustomFieldResponse,
    type IHasCustomFieldContainer,
} from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import {
    Email,
    type MinimalEmail,
} from "commonComponents/entity/email/InputEmail/InputEmail.types";
import { type IEmailEntryFormValues } from "commonComponents/entity/emailFormEntry/EmailFormEntry";
import { type ExternalEmailLookupFormValues } from "commonComponents/entity/externalEmailLookup/ExternalEmailLookup/ExternalEmailLookup.api.types";
import {
    type IJobAccess,
    type JobAccessEntity,
} from "commonComponents/entity/jobAccess/JobAccess/JobAccess.api.types";
import {
    type INotificationPreferenceData,
    type INotificationPreferenceGroupViewModel,
    type INotificationPreferenceRow,
} from "commonComponents/entity/notificationPreferences/NotificationPreferencesTable.api.types";
import { type JobsiteLinkingTypes } from "commonComponents/financial/Common/Accounting.types";
import { type StatusTypes } from "commonComponents/utilities/Status/StatusDisplay";

import { AccountingSetupDropdownData } from "entity/accounting/AccountingSettings/AccountingSettings.api.types";
import {
    type IRolePermissionFormValues,
    Role,
} from "entity/internalUser/RolePermissions/RolePermissions.api.types";
import { type VendorTypes } from "entity/vendor/common/vendor.types";

import blankSignatureImageSrc from "images/Common/claimNoPhoto.gif";

import type { default as TakeoffInfoExample } from "./InternalUser.api.takeoffs.json";
import type { default as VendorInfoExample } from "./InternalUser.api.vendorInfo.json";

export class JobsiteGroup {
    constructor(data: any) {
        this.id = data.id;
        this.name = data.name;
    }

    id: number;
    name: string;
}

export const unselectedRoleDropdownId = -1;
export type InternalUserFormActions =
    | undefined
    | "save"
    | "update"
    | "saveAndClose"
    | "saveAndNew"
    | "delete"
    | "setCredentials"
    | "connect"
    | "disconnect";

export interface IInternalUserFormValues extends IEmailEntryFormValues, ICellPhoneInputFormValues {
    firstName: string;
    lastName: string;
    phone?: string;
    cell: string;
    role: number;
    loginEnabled: boolean;
    userAccessNewJobs: boolean;
    addToAllExistingJobs?: boolean;
    sendInvitation: boolean;
    userEnabled: boolean;
    roles: IRolePermissionFormValues[];
    roleTreeData: BTSelectItem<IRoleExtraData>[];
    notificationPreferences: INotificationPreferenceRow[];
    jobAccess: JobAccessEntity | null;
    accountingEntityLinkingType: JobsiteLinkingTypes;
    createNewAccountingEntityOption: LinkingAction;
    username: string;
    enableConflictAlert: boolean;
    conflictAlertMaxCount: number;
    scheduleItems: number[];
    scheduleItemsFeed?: CalendarFeedItemViewModel;
    leadActivities: number[];
    leadActivitesFeed?: CalendarFeedItemViewModel;
    todos: number[];
    todosFeed?: CalendarFeedItemViewModel;
    tasks: number[];
    tasksFeed?: CalendarFeedItemViewModel;
    geofencing: boolean;
    requireNotesAtClockOut: boolean;
    defaultCostCodeId?: number;
    defaultLaborCost: number;
    defaultBillRate: number | null;
    messageSignature?: string;
    signatureImageTempId?: number;
    signatureImage?: string | null;
    messageSignatureImage?: string | null;
    tempFile?: BTServiceFileUpload;
    contactInfo: string;
    customFields: ICustomFieldFormValues[];
    payrollEmployeeId?: string;
    payrollJobID?: string;
    defaultPayrollItem?: string;
    isOutOfOffice: boolean;
    phoneCell: string;
    smsOptInStatus: SmsOptInStatus;
}

export interface IInternalUserStatusData {
    invitationDate?: moment.Moment;
    accountConfirmationDate?: moment.Moment;
    activationStatus?: InternalUserActivationStatus;
}

export class InternalUserEntity {
    constructor(data: any) {
        this.canView = data.canView;
        this.canEdit = data.canEdit;
        this.canAdd = data.canAdd;
        this.canDelete = data.canDelete;
        this.canEditJobAccess = data.canEditJobAccess;
        this.canViewNotifications = data.canViewNotifications;
        this.internalUser = new InternalUser(data.internalUser);
        this.jobGroups = data.jobGroups;
        this.failedFields = data.failedFields;
        this.notificationPreferences = data.notificationPreferences;
        this.isConnectedToGusto = data.isConnectedToGusto;
        this.hasTimeClockCostViewing = data.hasTimeClockCostViewing;

        if (data.vendorAccountUserDetails) {
            this.vendorAccountUserDetails = new VendorAccountUserDetails(
                data.vendorAccountUserDetails,
            );
        }
        if (data.takeoffUserDetails) {
            this.takeoffUserDetails = new TakeoffUserDetails(data.takeoffUserDetails);
        }

        this.payrollItems = data.payrollItems
            ? new AccountingSetupDropdownData(data.payrollItems)
            : undefined;

        this.isMfaEnabled = data.isMfaEnabled;

        this.hasCostCodesFeature = data.hasCostCodesFeature;
        this.isImpersonatingBuilder = data.isImpersonatingBuilder;
        this.canStartChatConversation = data.canStartChatConversation;
        this.canChangeEmail = data.canChangeEmail;
        this.hasLinkedUsers = data.hasLinkedUsers;
        this.orgOwnerCount = data.orgOwnerCount;
    }

    canView: boolean;
    canEdit: boolean;
    canAdd: boolean;
    canDelete: boolean;
    canEditJobAccess: boolean;
    canViewNotifications: boolean;
    internalUser: InternalUser;
    jobGroups: JobsiteGroup[];
    failedFields?: ServiceValidation[];
    notificationPreferences: INotificationPreferenceData;
    accountingEntity?: AccountingEntity;
    payrollSettingsEntity?: InternalUsersPayrollSettingsResponse;
    vendorAccountUserDetails?: VendorAccountUserDetails;
    takeoffUserDetails?: TakeoffUserDetails;
    isConnectedToGusto?: boolean;
    payrollItems?: AccountingSetupDropdownData;
    hasTimeClockCostViewing?: boolean;
    isMfaEnabled?: boolean;
    hasCostCodesFeature: boolean;
    isImpersonatingBuilder: boolean;
    canStartChatConversation: boolean;
    canChangeEmail: boolean;
    hasLinkedUsers: boolean;
    orgOwnerCount: number;
}

export class InternalUser implements IHasCustomFieldContainer {
    constructor(data: any) {
        this.id = data.loginId;
        this.firstName = data.firstName.value;
        this.lastName = data.lastName.value;
        this.primaryEmail = data.primaryEmail.value.emailAddress
            ? new Email(data.primaryEmail.value)
            : null;
        this.additionalEmails = data.additionalEmails.value.map(
            (email: string | MinimalEmail) => new Email(email),
        );
        this.cell = data.cell.value;
        this.phone = data.phone.value;
        this.countryCode = data.countryCode;
        this.smsOptInStatus = data.smsOptInStatus;
        this.cellPhoneNumber = data.cellPhoneNumber;
        this.loginEnabled = data.loginEnabled.value;
        this.activationStatus =
            data.activationStatus != null ? data.activationStatus.statusId : undefined;
        this.autoAddJobsites = data.autoAddJobsites?.value ?? false;
        this.addToAllExistingJobs = data.addToAllExistingJobs?.value ?? false;
        this.sendInvitation = data.sendInvitation;
        this.accountConfirmationDate = data.accountConfirmationDate
            ? moment(data.accountConfirmationDate)
            : undefined;
        this.invitationDate = data.invitationDate ? moment(data.invitationDate) : undefined;
        this.jobAccessCount = data.jobAccessCount;
        this.userId = data.userId;
        this.username = data.username;
        this.password = data.password;
        this.userEnabled = data.userEnabled.value;
        this.isSelf = data.isSelf;
        this.enableConflictAlert = data.enableConflictAlert;
        this.conflictAlertMaxCount = data.conflictAlertMaxCount;
        this.calendarFeedOptions = data.calendarFeedSummaryViewModel
            ? new CalendarFeedOptions(data.calendarFeedSummaryViewModel)
            : undefined;
        this.geofencingEnabled = data.geoFencingEnabled;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        this.requireNotesAtClockOut = data.requireNotesAtClockOut ?? false;
        this.defaultCostCodeId = data.defaultCostCodeId;
        this.defaultCostCodes = data.defaultLaborCostCodes.map(
            (cc: any) =>
                new BTSelectItem({
                    name: cc.title,
                    id: cc.costCodeId,
                    selected: cc.costCodeId === data.defaultLaborCostCodeId,
                }),
        );
        this.defaultLaborCost = Number(data.defaultLaborCost);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        this.defaultBillRate = data.defaultBillRate;
        this.byCostCodeHourlyTracking = data.byCostCodeHourlyTracking;
        this.messageSignature = data.messageSignature;
        this.signatureImage = data.messageSignatureImageUrl;
        this.contactInfo = data.contactInfo;
        this.customFields = data.customFields;
        this.customFieldOptions = data.customFieldOptions;
        this.customFieldAssociatedType = data.customFieldAssociatedType;
        this.customFieldsCanConfigure = data.customFieldsCanConfigure;

        this.cellEmailLookupLoadData = new CellEmailLookupEntity(data.cellEmailLookupLoadData);

        const selectedRoleId: number | undefined = data.role.value![0];

        const unselectedOption = new BTSelectItem<IRoleExtraData>({
            id: unselectedRoleDropdownId,
            name: "-- Choose Role --",
            selected: selectedRoleId === unselectedRoleDropdownId,
            extraData: undefined,
        });
        const roleSelectItems = data.role
            .options!.flatMap((r: Int32BTServiceGroupedListItem) => r.options)
            .map(
                (x: BTSelectItem<IRoleExtraData>) =>
                    new BTSelectItem<IRoleExtraData>({
                        ...x,
                        selected: parseInt(x.id) === selectedRoleId,
                    }),
            );
        roleSelectItems.unshift(unselectedOption);
        this.role = roleSelectItems;

        if (selectedRoleId) {
            const selectedRole = this.role.find(
                // e.key is falsely typed as string, when both e.key and selectedRoleId are numbers
                (e) => Number(e.key) === selectedRoleId,
            );
            this.roleName = selectedRole && selectedRole.title;
            this.roleJobStatusAccess = selectedRole?.extraData?.jobStatusAccess
                ? selectedRole.extraData?.jobStatusAccess
                : [];
        }

        this.roles = data.roles ? data.roles.map((role: any) => new Role(role)) : [];

        if (data.inviteStatus) {
            this.inviteStatus = data.inviteStatus.value;
        } else {
            this.inviteStatus = "User has not been invited";
        }
        if (data.inviteButton) {
            this.inviteButtonText = data.inviteButton.buttonName;
        } else if (this.inviteStatus.startsWith("Invite Accepted")) {
            this.inviteButtonText = undefined;
        } else {
            this.inviteButtonText = "Invite User";
        }

        this.isOutOfOffice = data.isOutOfOffice;
    }

    id: number;
    firstName: string;
    lastName: string;
    primaryEmail: Email | null;
    additionalEmails: Email[];
    cell: string;
    phone: string;
    loginEnabled: boolean;
    activationStatus: InternalUserActivationStatus;
    autoAddJobsites: boolean;
    addToAllExistingJobs: boolean;
    sendInvitation: boolean;
    accountConfirmationDate?: moment.Moment;
    invitationDate?: moment.Moment;
    cellEmailLookupLoadData: CellEmailLookupEntity;
    role: BTSelectItem<IRoleExtraData>[];
    roles: Role[];
    roleName?: string;
    roleJobStatusAccess: number[];
    inviteStatus: string;
    inviteButtonText?: string;
    jobAccessCount: number;
    userId: number;
    username: string;
    password: string;
    userEnabled: boolean;
    isSelf: boolean;
    enableConflictAlert: boolean;
    conflictAlertMaxCount: number;
    calendarFeedOptions?: CalendarFeedOptions;
    geofencingEnabled: boolean;
    requireNotesAtClockOut: boolean;
    defaultCostCodeId?: number;
    defaultCostCodes: BTSelectItem[];
    defaultLaborCost: number;
    defaultBillRate: number | null;
    byCostCodeHourlyTracking: boolean;
    messageSignature?: string;
    messageSignatureImage?: string;
    signatureImage?: string | null;
    contactInfo: string;
    customFields: ICustomFieldResponse[];
    customFieldOptions: ICustomFieldOptionsList;
    customFieldAssociatedType: CustomFieldAssociatedType;
    customFieldsCanConfigure: boolean;
    isOutOfOffice: boolean;
    cellPhoneNumber: string;
    smsOptInStatus: SmsOptInStatus;
    countryCode: Countries;
}

/**
 * Hand-maintained duplicate of the backend `CalendarFeedTypes` enum
 * (`Global.Core/Enums/Calendar/CalendarFeeds.cs`). The values are persisted and posted to the API,
 * so they must stay numerically in sync — write them explicitly rather than relying on
 * auto-increment.
 */
export enum FeedType {
    ScheduleItems = 0,
    LeadActivities = 1,
    Todos = 2,
    Tasks = 3,
}

/**
 * The persisted values of the backend `CalendarFeedTasksOptions` enum
 * (`Global.Core/Enums/Calendar/CalendarFeeds.cs`). Nothing selected means every task on the owner's jobs.
 *
 * The gaps at 3 and 5 are deliberate: those were the retired `AllTasksOnMyJobs` and
 * `TasksImAssignedOrWatching` scope values. Scope is now derived server-side from
 * {@link TasksFeedOption.OnlyAssignedToMe} and {@link TasksFeedOption.TasksImWatching} — neither means
 * all tasks on my jobs, both means assigned-or-watching. The surviving values were NOT renumbered,
 * because renumbering would silently reinterpret every already-saved feed row. Write them explicitly
 * rather than relying on auto-increment.
 */
export enum TasksFeedOption {
    OnlyAssignedToMe = 0,
    ExcludeCompleted = 1,
    IncludeJobName = 2,
    TasksImWatching = 4,
}

/**
 * Whether an option value exposes watched tasks, and so is gated on the WatchingForTasks flag. Offering
 * it with the flag off would be a back door into the feature — but a feed already SAVED with it keeps
 * being served, so callers must still render it in that case.
 */
export function exposesWatchedTasks(optionValue: number): boolean {
    // Callers hold plain numbers (BTSelectItem ids, Formik values), never the enum type, so
    // `no-unsafe-enum-comparison` needs the widening written out.
    return optionValue === (TasksFeedOption.TasksImWatching as number);
}

interface ICalendarFeedItem {
    FeedId: number;
    FeedOptions: number[];
    FeedType: FeedType;
    FeedUrl: string;
}

/**
 * One feed option, as `CalendarFeedOptionViewModel` sends it. `TasksOptions` is an ARRAY of these, unlike
 * the three sibling option sets, because JavaScript enumerates integer-like object keys in ascending
 * numeric order and the display order is 0, 4, 1, 2 — only an array can express it.
 */
interface ICalendarFeedOption {
    Value: number;
    Description: string;
}

interface ICalendarFeedOptionsData {
    CalendarFeedItems: ICalendarFeedItem[];
    ScheduleItemOptions: Record<number, string>;
    LeadActivityOptions: Record<number, string>;
    ToDosOptions: Record<number, string>;
    TasksOptions: ICalendarFeedOption[];
}

export class CalendarFeedOptions {
    constructor(data: ICalendarFeedOptionsData) {
        this.calendarFeedItems =
            data?.CalendarFeedItems.map((fi) => ({
                feedId: fi.FeedId,
                feedOptions: fi.FeedOptions,
                feedType: fi.FeedType,
                feedUrl: fi.FeedUrl,
            })) ?? [];

        this.scheduleFeedOptions = this.toSelectItems(
            data.ScheduleItemOptions,
            FeedType.ScheduleItems,
        );
        this.leadActivitiesFeedOptions = this.toSelectItems(
            data.LeadActivityOptions,
            FeedType.LeadActivities,
        );
        this.todoFeedOptions = this.toSelectItems(data.ToDosOptions, FeedType.Todos);

        const savedTasksOptions =
            this.calendarFeedItems.find((cfi) => cfi.feedType === FeedType.Tasks)?.feedOptions ??
            [];
        this.tasksFeedOptions = this.toOrderedSelectItems(data.TasksOptions, savedTasksOptions);
    }

    /**
     * The option id must be the backend enum VALUE (the dictionary key), not the array index. These agree
     * only while every option enum happens to be contiguous from 0; keying off the index would silently
     * corrupt saved rows the first time an option enum skips a value or hides a member.
     */
    private toSelectItems(options: Record<number, string>, feedType: FeedType): BTSelectItem[] {
        const savedOptions = this.calendarFeedItems.find(
            (cfi) => cfi.feedType === feedType,
        )?.feedOptions;

        return Object.entries(options).map(([key, value]) => {
            const id = Number(key);
            return new BTSelectItem({
                name: value,
                id,
                selected: savedOptions?.includes(id),
            });
        });
    }

    /**
     * Preserves the backend's array order, which IS the display order. The id is the backend enum value
     * (`Value`), never the array index — these values are non-contiguous (0, 4, 1, 2), so an index would
     * corrupt saved rows.
     *
     * Deliberately undefended against a non-array. Falling back to `[]` would leave the multi-select empty,
     * which BTSelect renders as a DISABLED control still captioned "-- All Tasks Selected --" while form
     * state holds no options at all — so the user's next save would quietly widen their feed to every task
     * they can see. A throw is the safer failure.
     */
    private toOrderedSelectItems(
        options: ICalendarFeedOption[],
        savedOptions: number[],
    ): BTSelectItem[] {
        return options.map(
            (option) =>
                new BTSelectItem({
                    name: option.Description,
                    id: option.Value,
                    selected: savedOptions.includes(option.Value),
                }),
        );
    }

    calendarFeedItems: CalendarFeedItemViewModel[];
    scheduleFeedOptions: BTSelectItem[];
    leadActivitiesFeedOptions: BTSelectItem[];
    todoFeedOptions: BTSelectItem[];
    tasksFeedOptions: BTSelectItem[];
}

export class InternalUserSaveRequest {
    constructor(values: IInternalUserFormValues) {
        this.firstName = values.firstName;
        this.lastName = values.lastName;
        this.username = values.username;
        this.userEnabled = values.userEnabled;
        this.email = (
            values.primaryEmail ? [values.primaryEmail, ...values.additionalEmails] : []
        ).map((e) => {
            return { emailAddress: e.emailAddress, friendlyName: e.friendlyName };
        });

        this.cell = values.cell;
        this.phone = values.phone ?? "";
        this.loginEnabled = values.loginEnabled;
        this.autoAddJobsites = values.userAccessNewJobs;
        this.addToAllExistingJobs = values.addToAllExistingJobs ?? false;
        this.sendInvitation = values.sendInvitation;
        this.enableConflictAlert = values.enableConflictAlert;
        this.conflictAlertMaxCount = values.conflictAlertMaxCount;
        this.geofencingEnabled = values.geofencing;
        this.requireNotesAtClockOut = values.requireNotesAtClockOut;
        this.defaultCostCodeId = values.defaultCostCodeId;
        this.defaultLaborCost = values.defaultLaborCost;
        this.defaultBillRate = values.defaultBillRate ?? null;
        this.messageSignature = values.messageSignature;
        this.messageSignatureImage =
            values.tempFile?.fileName ?? values.messageSignatureImage ?? "";
        this.messageSignatureImageUrl = values.tempFile?.url ?? "";
        this.hasSignatureImageChanged = values.tempFile?.hasChanged ?? false;
        this.signatureImageTempId = values.tempFile?.tempId ?? values.signatureImageTempId ?? 0;
        this.signatureImageExt = values.tempFile?.ext ?? "";
        this.contactInfo = values.contactInfo;
        this.customFields = values.customFields;
        this.role = values.role;
        this.jobs = values.jobAccess?.jobs;
        this.payrollEmployeeId = values.payrollEmployeeId;
        this.payrollJobID = values.payrollJobID;
        this.defaultPayrollItem = values.defaultPayrollItem;
        this.isOutOfOffice = values.isOutOfOffice;
        this.cellPhoneNumber = values.phoneCell;
        this.smsOptInStatus = values.smsOptInStatus;
        this.verificationRequest = values.verificationRequest;
    }

    firstName: string;
    lastName: string;
    username: string;
    userEnabled: boolean;
    email: ExternalEmailLookupFormValues;
    cell: string;
    phone: string;
    loginEnabled: boolean;
    autoAddJobsites: boolean;
    addToAllExistingJobs: boolean;
    sendInvitation: boolean;
    enableConflictAlert: boolean;
    conflictAlertMaxCount: number;
    geofencingEnabled: boolean;
    requireNotesAtClockOut: boolean;
    defaultCostCodeId?: number;
    defaultLaborCost: number;
    defaultBillRate: number | null;
    messageSignature?: string;
    messageSignatureImage: string;
    messageSignatureImageUrl: string;
    hasSignatureImageChanged: boolean;
    signatureImageTempId: number;
    signatureImageExt: string;
    contactInfo: string;
    customFields: ICustomFieldFormValues[];
    role: number;
    jobs?: IJobAccess[];
    payrollEmployeeId?: string;
    payrollJobID?: string;
    defaultPayrollItem?: string;
    isOutOfOffice: boolean;
    cellPhoneNumber: string;
    smsOptInStatus: SmsOptInStatus;
    verificationRequest: VerificationMessageType;
}

export class InternalUserEntitySaveRequest {
    constructor(values: IInternalUserFormValues, entity: InternalUserEntity) {
        this.internalUser = new InternalUserSaveRequest(values);

        const prefs: Record<string, INotificationPreferenceGroupViewModel> = {};
        if (entity.notificationPreferences) {
            entity.notificationPreferences.categories.forEach((category) => {
                category.groups.forEach((group) => {
                    group.preferences.forEach((pref) => {
                        const notificationPrefRow = values.notificationPreferences.find(
                            (row) => row.name === pref.name && row.type === pref.type,
                        )!;
                        pref.emailEnabled = notificationPrefRow.emailEnabled;
                        pref.textEnabled = notificationPrefRow.textEnabled;
                        pref.pushEnabled = notificationPrefRow.pushEnabled;
                        pref.allUsersEnabled = notificationPrefRow.allUsersEnabled;
                    });
                    // Merge groups with the same tabType (e.g., Bills and PurchaseOrders both use TabPermissions.PurchaseOrders)
                    const key = TabPermissions[group.tabType];
                    if (prefs[key]) {
                        prefs[key].preferences.push(...group.preferences);
                    } else {
                        prefs[key] = group;
                    }
                });
            });
        }
        this.pushEnabled =
            getCheckAllState(values.notificationPreferences, "pushEnabled") !== CheckAllState.None;

        this.notificationPrefs = prefs;
    }

    internalUser: InternalUserSaveRequest;
    notificationPrefs: Record<string, INotificationPreferenceGroupViewModel>;
    pushEnabled: boolean;
}

export class PasswordValidationResponse {
    constructor(data: any) {
        this.passwordStatus = data.passwordStatus;
        this.passwordStatusMessage = data.passwordStatusMessage;
        this.hasErrors = data.hasErrors ?? false;
    }
    passwordStatus: PasswordStatusType;
    passwordStatusMessage: string;
    hasErrors: boolean;
}

type PartnerInfoExampleType = typeof VendorInfoExample | typeof TakeoffInfoExample;

class PartnerUserDetails {
    constructor(data: PartnerInfoExampleType) {
        this.isBuilderConnected = data.isBuilderConnected;
        this.isConnected = data.isConnected;
        this.isReadOnly = data.isReadOnly;
        this.canConnect = data.canConnect;
        if (data.connectionDate) {
            this.connectionDate = moment(data.connectionDate);
        }
        this.isOnlyConnectedUser = data.isOnlyConnectedUser;
    }
    isBuilderConnected?: boolean;
    isConnected?: boolean;
    isReadOnly?: boolean;
    canConnect?: boolean;
    connectionDate?: moment.Moment;
    isOnlyConnectedUser?: boolean;
}

export class VendorAccountUserDetails extends PartnerUserDetails {
    constructor(data: typeof VendorInfoExample) {
        super(data);
        this.vendorType = data.vendorType;
        this.accountName = data.accountName;
        this.vendorAccountUserId = data.vendorAccountUserId;
        this.isOnlyConnectedUser = data.isOnlyConnectedUser;
    }
    vendorType?: VendorTypes;
    accountName?: string;
    vendorAccountUserId?: string;
    isOnlyConnectedUser?: boolean;
}

export class TakeoffUserDetails extends PartnerUserDetails {
    constructor(data: typeof TakeoffInfoExample) {
        super(data);
        this.partnerId = data.partnerId;
        this.externalUserId = data.externalUserId;
    }
    partnerId?: number;
    externalUserId?: string;
}

export class AccountingUserSyncResponse {
    constructor(data: any) {
        this.methodSuccessful = data.MethodSuccessful;
        this.message = data.Message;
    }
    methodSuccessful: boolean;
    message: string;
}

export enum PasswordStatusType {
    Success = 0,
    ConfirmationMismatch,
    MinimumLength,
    ExceedsMaximumLength,
    InvalidComposition,
    InvalidAuthentication,
    OwnerMatchesFriendsAndFamily,
    UnAuthorized,
}

export enum InternalUserTabKeys {
    Overview = "1",
    Permissions = "2",
    Notifications = "3",
    JobAccess = "4",
    Other = "5",
    Accounting = "6",
    Vendors = "7",
    Payroll = "8",
    Takeoff = "9",
    Messaging = "10",
    SecurityLogins = "11",
}

export enum InternalUserLoadAction {
    ChangePassword = 1,
}

export const InternalUserTabNames = {
    [InternalUserTabKeys.Overview]: "Overview",
    [InternalUserTabKeys.Permissions]: "Permissions",
    [InternalUserTabKeys.Notifications]: "Notifications",
    [InternalUserTabKeys.JobAccess]: "Job Access",
    [InternalUserTabKeys.Other]: "Other",
    [InternalUserTabKeys.Accounting]: "Accounting",
    [InternalUserTabKeys.Vendors]: "Vendors",
    [InternalUserTabKeys.Payroll]: "Payroll",
    [InternalUserTabKeys.SecurityLogins]: "Security & Login",
};

export interface IRoleExtraData {
    description: string;
    isSystemDefault: boolean;
    isHidden: boolean;
    disabledMessage: string;
    jobStatusAccess: number[];
    isMfaEnabled: boolean;
}

export class InviteProps {
    inviteSent: boolean;
    inviteStatus: string;
    inviteBtnText?: string;
}

export type PayrollEmployeeSyncStatus =
    | undefined
    | "Synced"
    | "Pending Setup in Gusto"
    | "Onboarding"
    | "Pending Setup";

export class InternalUsersPayrollSettingsResponse {
    constructor(data: any) {
        this.syncStatus = data.syncStatus;
        this.payrollIntegratioName = data.payrollIntegratioName;
        this.payrollEmployeeId = data.payrollEmployeeId;
        this.canView = data.canView;
        this.canUpdate = data.canUpdate;
        this.payrollLogoUrl = data.payrollLogoUrl;
        this.payrollJobID = data.payrollJobID;

        if (
            data.payrollEmployees &&
            data.payrollEmployees.value &&
            data.payrollEmployees.value.length > 0
        ) {
            this.payrollEmployees = data.payrollEmployees.value.map(
                (j: any) => new BTSelectItem(j),
            );
        }
        if (data.availableJobs && data.availableJobs.value && data.availableJobs.value.length > 0) {
            this.availableJobs = data.availableJobs.value.map((j: any) => new BTSelectItem(j));
        }
    }

    syncStatus: PayrollEmployeeSyncStatus;
    payrollIntegratioName: string;
    payrollEmployees?: BTSelectItem[];
    payrollEmployeeId: string;
    payrollLogoUrl: string;
    canUpdate: boolean;
    canView: boolean;
    availableJobs?: BTSelectItem[];
    payrollJobID: string;
}

export function getCategoryKey(categoryName: string): string {
    switch (categoryName) {
        case "Administrative":
        case "ADMINISTRATIVE":
            return "administrative";
        case "Files":
        case "FILES":
            return "files";
        case "Financial":
        case "FINANCIAL":
            return "financial";
        case "Messaging":
        case "MESSAGING":
            return "messaging";
        case "Project Management":
        case "PROJECT MANAGEMENT":
            return "projectManagement";
        case "Sales":
        case "SALES":
            return "sales";
        default:
            return "";
    }
}

export function getSubCategoryKey(subCategoryName: string): string {
    switch (subCategoryName) {
        case "Leads":
            return "leadPerms";
        case "Schedule":
            return "calendarPerms";
        case "Change Orders":
            return "changeOrderPerms";
        case "Daily Logs":
            return "dailyLogPerms";
        case "Selections":
            return "selectionPerms";
        case "Specifications":
            return "specificationsPerms";
        case "Time Clock":
            return "timeClockPerms";
        case "To-Do's":
            return "toDoPerms";
        case "Warranty":
            return "warrantyPerms";
        case "Admin":
        case "Internal User Information":
            return "adminPerms";
        case "Internal Users":
            return "contactPerms";
        case "Job Info":
        case "Job Information":
            return "jobsitePerms";
        case "Subs/Vendors":
        case "Sub/Vendor Information":
            return "subsPerms";
        case "Documents":
            return "documentPerms";
        case "Photos":
            return "photoPerms";
        case "Videos":
            return "videoPerms";
        case "Bidding":
        case "Bids":
            return "biddingPerms";
        case "Estimate General Items":
            return "estimatePerms";
        // Remove "Owner Invoices" case after ado-223723 is in prod. Temporarily here so this does not break during canary.
        case "Owner Invoices":
        case "Invoices":
            return "paymentPerms";
        case "Estimate":
        case "Estimates":
            return "proposalPerms";
        case "Purchase Orders":
            return "purchaseOrderPerms";
        case "Receipts":
            return "receiptPerms";
        case "Messages":
            return "emailPerms";
        case "RFIs":
            return "rfiPerms";
        case "Surveys":
            return "surveyPerms";
        case "Chat":
            return "chatPerms";
        case "Client Updates":
            return "clientUpdatePerms";
        case "Tasks":
            return "taskPerms";
        default:
            return "";
    }
}

export function showAllUserActivityCheckbox(
    type: NotificationTypes,
    role: IRolePermissionFormValues,
) {
    switch (type) {
        case NotificationTypes.LeadNotifyActivityReminder:
        case NotificationTypes.LeadProposal_AcceptDecline:
            return role.categories["sales"].subCategories["leadPerms"].permissions[
                "viewAllSalespersonLeadsAndActivities"
            ].enabled;
        case NotificationTypes.WARRANTYNEEDSFOLLOWUP:
            return role.categories["projectManagement"].subCategories["warrantyPerms"].permissions
                .view.enabled;
        case NotificationTypes.TimeClock_Adjustment:
        case NotificationTypes.TimeClock_OvertimeReached:
            return role.categories["projectManagement"].subCategories["timeClockPerms"].permissions[
                "viewOtherUsers"
            ].enabled;
        case NotificationTypes.NEWMESSAGE:
            return role.categories["messaging"].subCategories["emailPerms"].permissions["isGlobal"]
                .enabled;
        case NotificationTypes.SELECTION_DISCUSSION_ADDED:
        case NotificationTypes.CHANGEORDER_DISCUSSION_ADDED:
        case NotificationTypes.PO_DISCUSSION_ADDED:
        case NotificationTypes.WARRANTY_DISCUSSION_ADDED:
        case NotificationTypes.Bid_DiscussionAdded:
        case NotificationTypes.Daily_Log_Discussion_Added:
        case NotificationTypes.Schedule_Item_Discussion_Added:
        case NotificationTypes.TO_DO_DISCUSSION_ADDED:
        case NotificationTypes.OwnerPaymentDiscussionAdded:
        case NotificationTypes.PhotoCommentAdded:
        case NotificationTypes.DocumentCommentAdded:
        case NotificationTypes.VideoCommentAdded:
        case NotificationTypes.CREDITMEMO_DISCUSSION_ADDED:
        case NotificationTypes.BILL_DISCUSSION_ADDED:
        case NotificationTypes.BillRejection:
        case NotificationTypes.DepositDiscussionAdded:
        case NotificationTypes.ClientUpdateDiscussionAdded:
        case NotificationTypes.Task_Discussion_Added:
            return true;
        default:
            return false;
    }
}

// Must match ImageHandler.Common.NoPhoto, which the server sends when no signature is uploaded.
export const blankSignatureImage = "/images/Common/claimNoPhoto.gif";

export const blankSignatureImagePlaceholder = blankSignatureImageSrc;

export type PayrollTabFormActions = undefined | "sync" | "add";

export interface IUserStatus extends IInternalUserStatus {
    statusDate?: Moment;
}

export interface IInternalUserStatus {
    statusType: StatusTypes;
    statusMessage: string;
}
